import {readLocalItem, writeLocalItem} from "../../core/browserStorage";

/**
 * How often a sender may meet the send screen's upsell card (kitecorp/transferify-ui#2278): once per
 * seven days, across every wall rather than once per wall.
 *
 * Pure domain plus one storage seam, no React (epic #26).
 *
 * <b>The record is per SENDER, keyed by account id where there is one.</b> The owner's rule is "per
 * account, not per browser", and since ui#2555 the account's own window IS behind the api
 * (`POST /offers/UPSELL_CARD/claim`, kitecorp/transferify-api-service#1774) — which is what finally
 * answers the case this file could not: the same account in two browsers. This record is now the
 * FALLBACK, for a claim nobody answered, and the whole rule for a guest, who has no account for a window
 * to belong to. It stays keyed by the account so a shared browser cannot silence one sender with
 * another's showing.
 */
export const UPSELL_CARD_SHOWN_STORAGE_KEY = "upsell.cardShown.v1";

/** Seven days, to the millisecond (owner 2026-09-16). */
export const UPSELL_CARD_QUIET_MS = 7 * 24 * 60 * 60 * 1000;

/** The key a signed-out sender's record is kept under: they have no account to key on. */
export const GUEST_SENDER_KEY = "guest";

/** The account this record belongs to, or the guest key for a sender who has not signed in. */
export function upsellSenderKey(accountId: string | null): string {
	return accountId === null || accountId === "" ? GUEST_SENDER_KEY : accountId;
}

/**
 * Whether the card may be shown again.
 *
 * A record from the FUTURE reads as due rather than as quiet forever: a clock that moved backwards
 * (a corrected device, a restored profile) would otherwise silence the card permanently.
 */
export function upsellCardDue(lastShownAt: number | null, now: number): boolean {
	return lastShownAt === null || now - lastShownAt >= UPSELL_CARD_QUIET_MS || now < lastShownAt;
}

/** What a wall the composer just met may do: arm the card, ask the account's window, or nothing. */
export type UpsellCardArming = "arm" | "askTheWindow" | "refuse";

/**
 * Whether a wall the composer just met may arm the card, and WHO decides.
 *
 * <b>The quiet window decides whether the FIRST card of a visit may appear, and nothing after that</b>
 * (owner 2026-09-17, reviewing ui#2514 on the preview). The record is written the moment a card OPENS,
 * so once one is on screen every later wall of that same visit met a spent window and was turned away —
 * which left the card saying "password" while the sender was clicking Zero-Knowledge. A card that names
 * the wrong control is worse than no card, because the whole premise since #2514 is that it shows what
 * was clicked. That is the `"arm"` answer for a showing card: no window is consulted at all, and none is
 * spent, because the presentation this visit already has one.
 *
 * <b>For the first card, an ACCOUNT is asked and a browser is read</b> (ui#2555,
 * kitecorp/transferify-api-service#1774). A signed-in sender's window is a fact about their account that
 * two browsers must agree on, so it can only be answered by the api — this module says "ask", and
 * `upsell/offerShowWindow` says what to do with the answer, including the failure that falls back to the
 * record below. A guest has no account for a window to belong to, so their own browser's record is the
 * whole rule.
 *
 * Re-arming only updates what the card SAYS. Whether it is out or tucked belongs to `slideOutCards.ts`,
 * which spends a card's one self-opening the first time it takes it: an open card stays open, and a card
 * the reader tucked stays a chip rather than sliding back over them.
 *
 * @param showing whether a card this visit armed is on screen — the only thing that lifts the window
 * @param signedIn whether this reader has an account for the api's window to belong to
 */
export function upsellCardArming(
	showing: boolean, signedIn: boolean, lastShownAt: number | null, now: number,
): UpsellCardArming {
	if (showing) {
		return "arm";
	}
	if (signedIn) {
		return "askTheWindow";
	}
	return upsellCardDue(lastShownAt, now) ? "arm" : "refuse";
}

/** Every sender's last showing, or an empty record where nothing is readable. */
function storedShowings(): Record<string, unknown> {
	const raw = readLocalItem(UPSELL_CARD_SHOWN_STORAGE_KEY);
	if (raw === null) {
		return {};
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
			? parsed as Record<string, unknown>
			: {};
	} catch {
		return {};
	}
}

/** When this sender last met the card, or null — including every unreadable state. */
export function readUpsellCardShown(sender: string): number | null {
	const at = storedShowings()[sender];
	return typeof at === "number" && Number.isFinite(at) ? at : null;
}

/**
 * Records that this sender met the card. Dismissing counts as meeting it, so the caller records the
 * showing, never the reading.
 *
 * @returns whether the record was actually kept. A browser that declines storage must cost one extra
 *   card, never the send the card sits beside, so the refusal is reported rather than thrown.
 */
export function rememberUpsellCardShown(sender: string, at: number): boolean {
	return writeLocalItem(UPSELL_CARD_SHOWN_STORAGE_KEY, JSON.stringify({...storedShowings(), [sender]: at}));
}
